import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Row,
    Col,
    Form,
    Button,
    Badge,
    Spinner,
    Alert,
    Modal,
    ListGroup,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import surveyService from '@/shared/services/surveyService';
import { useTerm } from '@/shared/contexts/TermContext';
import { inProgramDisplayOrder } from '@/shared/utils/programOrder';

const QUESTION_TYPES = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'likert', label: 'Likert Scale (1-5)' },
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'select', label: 'Single Select' },
    { value: 'multiselect', label: 'Multi Select' },
    { value: 'checkbox', label: 'Checkbox' },
];

const AUDIENCE_OPTIONS = [
    { value: 'faculty', label: 'Faculty' },
    { value: 'students', label: 'Students' },
    { value: 'peer_supporters', label: 'Peer Supporters' },
    { value: 'all', label: 'All' },
];

const PROGRAM_OPTIONS = [
    { value: '', label: 'All Programs' },
    ...inProgramDisplayOrder(
        [
            { value: 'si', label: 'SI - Supplemental Instruction' },
            { value: 'ta', label: 'TA - Teaching Assistant' },
            { value: 'la', label: 'LA - Learning Assistant' },
        ],
        (option) => option.value
    ),
];

const LIKERT_LABELS = [
    'Strongly Disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly Agree',
];

function createEmptyQuestion(index) {
    return {
        questionId: `q_${Date.now()}_${index}`,
        text: '',
        type: 'text',
        options: [],
        required: false,
        helpText: '',
    };
}

function SurveyBuilderPage() {
    const navigate = useNavigate();
    const { currentTermCode } = useTerm();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [targetAudience, setTargetAudience] = useState('all');
    const [program, setProgram] = useState('');
    const [allowAnonymous, setAllowAnonymous] = useState(false);
    const [openDate, setOpenDate] = useState('');
    const [closeDate, setCloseDate] = useState('');
    const [questions, setQuestions] = useState([createEmptyQuestion(0)]);
    const [isActive, setIsActive] = useState(true);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);

    // Template modal state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const loadTemplates = useCallback(async () => {
        try {
            setTemplatesLoading(true);
            // Templates are inactive surveys used as starting points
            const data = await surveyService.getSurveys();
            // Filter to template-like surveys (inactive ones without a term)
            const templateSurveys = data.filter((s) => !s.isActive || !s.term);
            setTemplates(templateSurveys);
        } catch (_err) {
            // template load is best-effort; the modal shows an empty state
        } finally {
            setTemplatesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (showTemplateModal) {
            loadTemplates();
        }
    }, [showTemplateModal, loadTemplates]);

    const handleSelectTemplate = (template) => {
        setTitle(template.title);
        setDescription(template.description || '');
        setTargetAudience(template.targetAudience || 'all');
        setProgram(template.program || '');
        setAllowAnonymous(template.allowAnonymous || false);
        setQuestions(
            (template.questions || []).map((q, idx) => ({
                ...q,
                questionId: q.questionId || `q_${Date.now()}_${idx}`,
            }))
        );
        setShowTemplateModal(false);
    };

    const updateQuestion = (index, field, value) => {
        setQuestions((prev) =>
            prev.map((q, i) => (i === index ? { ...q, [field]: value } : q))
        );
    };

    const addQuestion = () => {
        setQuestions((prev) => [...prev, createEmptyQuestion(prev.length)]);
    };

    const removeQuestion = (index) => {
        setQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    const moveQuestion = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= questions.length) return;
        setQuestions((prev) => {
            const updated = [...prev];
            const temp = updated[index];
            updated[index] = updated[newIndex];
            updated[newIndex] = temp;
            return updated;
        });
    };

    const addOption = (questionIndex) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? { ...q, options: [...(q.options || []), ''] }
                    : q
            )
        );
    };

    const updateOption = (questionIndex, optionIndex, value) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? {
                          ...q,
                          options: q.options.map((opt, oi) =>
                              oi === optionIndex ? value : opt
                          ),
                      }
                    : q
            )
        );
    };

    const removeOption = (questionIndex, optionIndex) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? {
                          ...q,
                          options: q.options.filter(
                              (_, oi) => oi !== optionIndex
                          ),
                      }
                    : q
            )
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!title.trim()) {
            setError('Survey title is required.');
            return;
        }
        if (!targetAudience) {
            setError('Target audience is required.');
            return;
        }
        if (questions.length === 0) {
            setError('At least one question is required.');
            return;
        }
        const emptyQuestions = questions.filter((q) => !q.text.trim());
        if (emptyQuestions.length > 0) {
            setError('All questions must have text.');
            return;
        }

        // Validate options for select/multiselect
        const optionQuestions = questions.filter(
            (q) => q.type === 'select' || q.type === 'multiselect'
        );
        for (const q of optionQuestions) {
            if (!q.options || q.options.filter((o) => o.trim()).length < 2) {
                setError(`Question "${q.text}" needs at least 2 options.`);
                return;
            }
        }

        try {
            setSubmitting(true);
            setError(null);

            const surveyData = {
                title: title.trim(),
                description: description.trim(),
                targetAudience,
                program: program || undefined,
                term: currentTermCode || undefined,
                allowAnonymous,
                isActive,
                openDate: openDate || undefined,
                closeDate: closeDate || undefined,
                questions: questions.map((q) => ({
                    questionId: q.questionId,
                    text: q.text.trim(),
                    type: q.type,
                    options:
                        q.type === 'select' || q.type === 'multiselect'
                            ? q.options.filter((o) => o.trim())
                            : undefined,
                    required: q.required,
                    helpText: q.helpText?.trim() || undefined,
                })),
            };

            await surveyService.createSurvey(surveyData);
            navigate('/surveys');
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to create survey. Please try again.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const renderPreview = () => (
        <div>
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h2 className="fs-4">{title || 'Untitled Survey'}</h2>
                    {description && <p className="text-muted">{description}</p>}
                </Card.Body>
            </Card>
            {questions.map((q, idx) => (
                <Card key={idx} className="mb-3 border-0 shadow-sm">
                    <Card.Body>
                        <Form.Label className="fw-bold">
                            {q.text || 'Untitled Question'}
                            {q.required && (
                                <span className="text-danger ms-1">*</span>
                            )}
                        </Form.Label>
                        {q.helpText && (
                            <Form.Text className="d-block mb-2 text-muted">
                                {q.helpText}
                            </Form.Text>
                        )}
                        {q.type === 'text' && (
                            <Form.Control type="text" disabled />
                        )}
                        {q.type === 'textarea' && (
                            <Form.Control as="textarea" rows={3} disabled />
                        )}
                        {q.type === 'likert' && (
                            <div className="d-flex justify-content-between mt-2">
                                {LIKERT_LABELS.map((label, i) => (
                                    <div key={i} className="text-center">
                                        <Form.Check
                                            type="radio"
                                            aria-label={label}
                                            disabled
                                            className="d-flex justify-content-center"
                                        />
                                        <small className="text-muted">
                                            {label}
                                        </small>
                                    </div>
                                ))}
                            </div>
                        )}
                        {q.type === 'rating' && (
                            <div className="d-flex gap-3 mt-2">
                                {[1, 2, 3, 4, 5].map((v) => (
                                    <Form.Check
                                        key={v}
                                        type="radio"
                                        label={v}
                                        disabled
                                        inline
                                    />
                                ))}
                            </div>
                        )}
                        {q.type === 'select' && (
                            <Form.Select disabled>
                                <option>Select an option...</option>
                                {(q.options || []).map((opt, i) => (
                                    <option key={i}>{opt}</option>
                                ))}
                            </Form.Select>
                        )}
                        {q.type === 'multiselect' &&
                            (q.options || []).map((opt, i) => (
                                <Form.Check
                                    key={i}
                                    type="checkbox"
                                    label={opt}
                                    disabled
                                    className="mb-1"
                                />
                            ))}
                        {q.type === 'checkbox' && (
                            <Form.Check type="checkbox" label="Yes" disabled />
                        )}
                    </Card.Body>
                </Card>
            ))}
        </div>
    );

    return (
        <Container className="py-4">
            <div className="mb-4">
                <Button
                    variant="link"
                    className="p-0 text-muted"
                    onClick={() => navigate('/surveys')}
                >
                    &larr; Back to Surveys
                </Button>
            </div>

            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>{previewMode ? 'Preview Survey' : 'Create Survey'}</h1>
                    <p className="text-muted">
                        {previewMode
                            ? 'This is how the survey will appear to respondents'
                            : 'Build a new survey for your programs'}
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowTemplateModal(true)}
                        disabled={previewMode}
                    >
                        Start from Template
                    </Button>
                    <Button
                        variant={previewMode ? 'primary' : 'outline-primary'}
                        onClick={() => setPreviewMode(!previewMode)}
                    >
                        {previewMode ? 'Edit' : 'Preview'}
                    </Button>
                </div>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {previewMode ? (
                renderPreview()
            ) : (
                <Form noValidate onSubmit={handleSubmit}>
                    {/* Survey Details */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <h2 className="mb-3 fs-5">Survey Details</h2>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <Form.Group controlId="survey-title">
                                        <Form.Label>Title *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={title}
                                            onChange={(e) =>
                                                setTitle(e.target.value)
                                            }
                                            placeholder="Enter survey title"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12} className="mb-3">
                                    <Form.Group controlId="survey-description">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={description}
                                            onChange={(e) =>
                                                setDescription(e.target.value)
                                            }
                                            placeholder="Describe the purpose of this survey"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group controlId="survey-target-audience">
                                        <Form.Label>
                                            Target Audience *
                                        </Form.Label>
                                        <Form.Select
                                            value={targetAudience}
                                            onChange={(e) =>
                                                setTargetAudience(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {AUDIENCE_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group controlId="survey-program">
                                        <Form.Label>Program</Form.Label>
                                        <Form.Select
                                            value={program}
                                            onChange={(e) =>
                                                setProgram(e.target.value)
                                            }
                                        >
                                            {PROGRAM_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group className="mt-4">
                                        <Form.Check
                                            id="survey-allow-anonymous"
                                            type="checkbox"
                                            label="Allow anonymous responses"
                                            checked={allowAnonymous}
                                            onChange={(e) =>
                                                setAllowAnonymous(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <Form.Check
                                            id="survey-is-active"
                                            type="checkbox"
                                            label="Deploy immediately (active)"
                                            checked={isActive}
                                            onChange={(e) =>
                                                setIsActive(e.target.checked)
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group controlId="survey-open-date">
                                        <Form.Label>Open Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={openDate}
                                            onChange={(e) =>
                                                setOpenDate(e.target.value)
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group controlId="survey-close-date">
                                        <Form.Label>Close Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={closeDate}
                                            onChange={(e) =>
                                                setCloseDate(e.target.value)
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Questions */}
                    <h2 className="mb-3 fs-5">
                        Questions ({questions.length})
                    </h2>

                    {questions.map((question, idx) => (
                        <Card
                            key={question.questionId}
                            className="border-0 shadow-sm mb-3"
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Badge bg="secondary">
                                        Question {idx + 1}
                                    </Badge>
                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() =>
                                                moveQuestion(idx, -1)
                                            }
                                            disabled={idx === 0}
                                            title="Move up"
                                        >
                                            &uarr;
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => moveQuestion(idx, 1)}
                                            disabled={
                                                idx === questions.length - 1
                                            }
                                            title="Move down"
                                        >
                                            &darr;
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeQuestion(idx)}
                                            disabled={questions.length <= 1}
                                            title="Remove question"
                                        >
                                            &times;
                                        </Button>
                                    </div>
                                </div>
                                <Row>
                                    <Col md={8} className="mb-2">
                                        <Form.Group
                                            controlId={`question-text-${idx}`}
                                        >
                                            <Form.Label>
                                                Question Text
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={question.text}
                                                onChange={(e) =>
                                                    updateQuestion(
                                                        idx,
                                                        'text',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Enter your question"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <Form.Group
                                            controlId={`question-type-${idx}`}
                                        >
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                value={question.type}
                                                onChange={(e) =>
                                                    updateQuestion(
                                                        idx,
                                                        'type',
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {QUESTION_TYPES.map((t) => (
                                                    <option
                                                        key={t.value}
                                                        value={t.value}
                                                    >
                                                        {t.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={8} className="mb-2">
                                        <Form.Group
                                            controlId={`question-help-${idx}`}
                                        >
                                            <Form.Label>
                                                Help Text (optional)
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={question.helpText || ''}
                                                onChange={(e) =>
                                                    updateQuestion(
                                                        idx,
                                                        'helpText',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Additional instructions for respondents"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <Form.Group className="mt-4">
                                            <Form.Check
                                                id={`question-required-${idx}`}
                                                type="checkbox"
                                                label="Required"
                                                checked={question.required}
                                                onChange={(e) =>
                                                    updateQuestion(
                                                        idx,
                                                        'required',
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Options editor for select/multiselect */}
                                {(question.type === 'select' ||
                                    question.type === 'multiselect') && (
                                    <div className="mt-3">
                                        <Form.Label>Options</Form.Label>
                                        {(question.options || []).map(
                                            (opt, optIdx) => (
                                                <div
                                                    key={optIdx}
                                                    className="d-flex gap-2 mb-2"
                                                >
                                                    <Form.Control
                                                        type="text"
                                                        value={opt}
                                                        onChange={(e) =>
                                                            updateOption(
                                                                idx,
                                                                optIdx,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={`Option ${optIdx + 1}`}
                                                        size="sm"
                                                    />
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() =>
                                                            removeOption(
                                                                idx,
                                                                optIdx
                                                            )
                                                        }
                                                    >
                                                        &times;
                                                    </Button>
                                                </div>
                                            )
                                        )}
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => addOption(idx)}
                                        >
                                            + Add Option
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    ))}

                    <Button
                        variant="outline-primary"
                        onClick={addQuestion}
                        className="mb-4 w-100"
                    >
                        + Add Question
                    </Button>

                    {/* Submit */}
                    <div className="d-flex justify-content-between">
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate('/surveys')}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Creating...
                                </>
                            ) : (
                                'Create Survey'
                            )}
                        </Button>
                    </div>
                </Form>
            )}

            {/* Template Modal */}
            <Modal
                aria-labelledby="sb-modal-title-1"
                show={showTemplateModal}
                onHide={() => setShowTemplateModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="sb-modal-title-1">
                        Start from Template
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {templatesLoading ? (
                        <div className="text-center py-4">
                            <Spinner
                                animation="border"
                                variant="primary"
                                size="sm"
                            />
                            <p className="mt-2 text-muted">
                                Loading templates...
                            </p>
                        </div>
                    ) : templates.length === 0 ? (
                        <p className="text-muted text-center py-4">
                            No templates available. Templates are created as
                            inactive surveys.
                        </p>
                    ) : (
                        <ListGroup>
                            {templates.map((template) => (
                                <ListGroup.Item
                                    key={template._id}
                                    action
                                    onClick={() =>
                                        handleSelectTemplate(template)
                                    }
                                    className="d-flex justify-content-between align-items-start"
                                >
                                    <div>
                                        <h6 className="mb-1">
                                            {template.title}
                                        </h6>
                                        <small className="text-muted">
                                            {template.description}
                                        </small>
                                        <div className="mt-1">
                                            <Badge
                                                bg="info"
                                                text="dark"
                                                className="me-1"
                                            >
                                                {template.targetAudience}
                                            </Badge>
                                            <Badge bg="secondary">
                                                {template.questions?.length ||
                                                    0}{' '}
                                                questions
                                            </Badge>
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowTemplateModal(false)}
                    >
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default SurveyBuilderPage;
